/* Patch 19 — Unified Action Queue Widget
   ================================================
   Single-glance surface of every actionable Decision Center in the
   ERP. Polls GET /api/action-queue/summary and renders priority-
   sorted rows with deep links so the CFO never has to crawl six
   pages to discover where attention is required. */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, AlertOctagon, AlertTriangle, Bell, Info, ArrowRight,
  WifiOff,
} from 'lucide-react';
import useActionQueueSocket from '../hooks/useActionQueueSocket';

const SEVERITY_THEME = {
  critical: {
    chip: 'bg-rose-100 text-rose-800 border-rose-200',
    dot:  'bg-rose-500',
    icon: AlertOctagon,
    rank: 1,
  },
  high: {
    chip: 'bg-amber-100 text-amber-800 border-amber-200',
    dot:  'bg-amber-500',
    icon: AlertTriangle,
    rank: 2,
  },
  medium: {
    chip: 'bg-sky-100 text-sky-800 border-sky-200',
    dot:  'bg-sky-500',
    icon: Bell,
    rank: 3,
  },
  low: {
    chip: 'bg-slate-100 text-slate-700 border-slate-200',
    dot:  'bg-slate-400',
    icon: Info,
    rank: 4,
  },
};

export default function UnifiedActionQueueWidget() {
  const { summary: data, status } = useActionQueueSocket();
  const loading = !data && (status === 'connecting' || status === 'reconnecting');
  const error = null; // Hook degrades to polling silently; never errors.

  if (loading) {
    return (
      <div
        className="rounded-lg border border-slate-200 bg-white p-4 flex items-center gap-2 text-sm text-slate-500"
        data-testid="action-queue-widget-loading"
      >
        <Loader2 className="animate-spin" size={14} /> Loading action queue…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 inline-flex items-center gap-2"
        data-testid="action-queue-widget-error"
      >
        <AlertTriangle size={14} /> {error}
      </div>
    );
  }

  if (!data) return null;

  // Sort: severity rank ASC, then count DESC. Zero-count rows fall
  // to the bottom but are still rendered so the operator sees the
  // queue exists and is currently empty.
  const queues = [...(data.queues || [])].sort((a, b) => {
    const ra = SEVERITY_THEME[a.severity]?.rank ?? 99;
    const rb = SEVERITY_THEME[b.severity]?.rank ?? 99;
    if (ra !== rb) return ra - rb;
    return (b.count || 0) - (a.count || 0);
  });

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white shadow-sm"
      data-testid="action-queue-widget"
    >
      <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2">
            Unified Action Queue
            <LiveBadge status={status} />
          </div>
          <h3 className="text-base font-bold text-slate-900">
            Where attention is required
          </h3>
        </div>
        <div className="flex items-center gap-2 text-xs" data-testid="action-queue-severity-bar">
          <SeverityPill kind="critical" count={data.critical} />
          <SeverityPill kind="high"     count={data.high} />
          <SeverityPill kind="medium"   count={data.medium} />
          <SeverityPill kind="low"      count={data.low} />
        </div>
      </header>

      <ul className="divide-y divide-slate-100" data-testid="action-queue-list">
        {queues.map((q) => {
          const theme = SEVERITY_THEME[q.severity] || SEVERITY_THEME.low;
          const Icon  = theme.icon;
          const isEmpty = !q.count;
          return (
            <li
              key={q.key}
              data-testid={`action-queue-row-${q.key}`}
              className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${isEmpty ? 'opacity-50' : 'hover:bg-slate-50'}`}
            >
              <span className={`flex items-center justify-center w-7 h-7 rounded-full ${theme.dot}`}>
                <Icon className="w-3.5 h-3.5 text-white" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">
                  {q.label}
                </div>
                <div className="text-[11px] text-slate-500">
                  Deep-link to <span className="font-mono">{q.link}</span>
                </div>
              </div>
              <span
                className={`px-2 py-0.5 rounded text-xs font-mono font-bold border ${theme.chip}`}
                data-testid={`action-queue-count-${q.key}`}
              >
                {q.count}
              </span>
              <Link
                to={q.link}
                data-testid={`action-queue-open-${q.key}`}
                className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded px-2 py-1 ${
                  isEmpty
                    ? 'bg-slate-100 text-slate-400 pointer-events-none'
                    : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
              >
                Open <ArrowRight size={10} />
              </Link>
            </li>
          );
        })}
      </ul>

      <footer className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500 flex items-center justify-between">
        <span>{data.total} total action{data.total === 1 ? '' : 's'} pending</span>
        {data.generated_at && (
          <span className="font-mono">{(data.generated_at || '').slice(11, 19)} UTC</span>
        )}
      </footer>
    </section>
  );
}


function SeverityPill({ kind, count }) {
  const theme = SEVERITY_THEME[kind] || SEVERITY_THEME.low;
  return (
    <span
      data-testid={`action-queue-pill-${kind}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${theme.chip} font-semibold uppercase tracking-wider`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${theme.dot}`} />
      {kind} {count}
    </span>
  );
}


// Patch 24 — Live / Reconnecting / Offline badge driven by the
// useActionQueueSocket hook. Visible on every dashboard widget so
// operators can immediately tell if they're looking at fresh data.
function LiveBadge({ status }) {
  if (status === 'live') {
    return (
      <span
        data-testid="action-queue-live-badge"
        title="Real-time feed connected"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 normal-case tracking-normal text-[10px] font-semibold"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        Live
      </span>
    );
  }
  if (status === 'reconnecting' || status === 'connecting') {
    return (
      <span
        data-testid="action-queue-reconnecting-badge"
        title="Reconnecting…"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700 normal-case tracking-normal text-[10px] font-semibold"
      >
        <Loader2 className="w-2.5 h-2.5 animate-spin" />
        Reconnecting
      </span>
    );
  }
  return (
    <span
      data-testid="action-queue-offline-badge"
      title="Real-time feed unavailable — polling every 30s"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-300 bg-slate-100 text-slate-600 normal-case tracking-normal text-[10px] font-semibold"
    >
      <WifiOff className="w-2.5 h-2.5" />
      Polling
    </span>
  );
}
